import React, {ReactNode} from "react";
import Link from "next/link";
import {Tooltip} from "@/components/tailwindComponents/Tooltip";
import {useCompanyInfo} from "@/hooks/useCompanyInfo";
import {ArrowLeftIcon} from "@heroicons/react/24/outline";


interface AuthWrapperProps {
    label: string
    children: ReactNode;
}

const AuthWrapper: React.FC<AuthWrapperProps> = ({children, label}) => {
    const {logo, name} = useCompanyInfo();

    return (
        <div
            className="
                relative
                flex
                min-h-dvh
                w-full
                items-center
                justify-center
                overflow-hidden

                bg-gradient-to-br
                from-gray-400
                via-gray-300
                to-gray-500

                px-0

                sm:px-4
            "
        >
            {/* Background light */}
            <div
                className="
                    pointer-events-none
                    absolute
                    -left-40
                    -top-40
                    size-[500px]
                    rounded-full
                    bg-white/40
                    blur-[100px]
                "
            />

            {/* Background dark glow */}
            <div
                className="
                    pointer-events-none
                    absolute
                    -bottom-40
                    -right-40
                    size-[500px]
                    rounded-full
                    bg-gray-600/20
                    blur-[100px]
                "
            />

            {/* Glass Card */}
            <div
                className="
                    relative
                    z-10

                    flex
                    min-h-dvh
                    w-full
                    flex-col
                    items-center
                    overflow-hidden

                    rounded-none
                    border-0

                    bg-white/75

                    px-5
                    py-6

                    backdrop-blur-2xl

                    shadow-none

                    sm:min-h-0
                    sm:max-w-lg
                    sm:rounded-[28px]
                    sm:border
                    sm:border-white/80
                    sm:bg-white/60
                    sm:px-8
                    sm:py-6

                    sm:shadow-[
                        0_25px_70px_rgba(0,0,0,0.22),
                        0_8px_25px_rgba(0,0,0,0.10),
                        inset_0_1px_1px_rgba(255,255,255,1),
                        inset_0_-1px_2px_rgba(0,0,0,0.08)
                    ]
                "
            >
                {/* Top Glass Reflection */}
                <div
                    className="
                        pointer-events-none
                        absolute
                        inset-x-6
                        top-0
                        hidden
                        h-[2px]
                        rounded-full
                        bg-gradient-to-r
                        from-transparent
                        via-white
                        to-transparent
                        opacity-90

                        sm:block
                    "
                />

                {/* Inner Glass Border */}
                <div
                    className="
                        pointer-events-none
                        absolute
                        inset-[1px]
                        hidden
                        rounded-[27px]
                        border
                        border-white/40

                        sm:block
                    "
                />

                {/* Header */}
                <div
                    className="
                        relative
                        z-10
                        flex
                        w-full
                        items-center
                        justify-between
                    "
                >
                    {/* Logo */}
                    <Link
                        prefetch={false}
                        href="/"
                        className="inline-block"
                    >
                        <div className="group relative h-14 w-auto">
                            <img
                                className="
                                    h-14
                                    w-auto
                                    object-contain
                                    drop-shadow-lg
                                "
                                src={logo || "/default-logo.png"}
                                alt={name || "logo"}
                            />

                            <Tooltip content={name || "صفحه اصلی"}/>
                        </div>
                    </Link>

                    {/* Back to Home */}
                    <Link
                        href="/"
                        className="
                            inline-flex
                            items-center
                            justify-end
                            rounded-md

                            text-xs
                            text-gray-600

                            transition-colors
                            duration-200

                            hover:text-gray-950
                        "
                    >
                        <p
                            className="
                                inline-flex
                                items-center
                                gap-x-1.5

                                transition-transform
                                duration-200

                                hover:scale-105
                            "
                        >
                            <b>بازگشت به صفحه اصلی</b>

                            <ArrowLeftIcon className="size-3"/>
                        </p>
                    </Link>
                </div>

                {/* Title */}
                <div
                    className="
                        relative
                        z-10
                        mt-5
                        mb-5
                        w-full
                    "
                >
                    <h2
                        className="
                            text-center
                            text-2xl/9
                            font-bold
                            text-gray-900
                        "
                    >
                        {label}
                    </h2>
                </div>

                {/* Content */}
                <div
                    className="
                        relative
                        z-10
                        w-full
                    "
                >
                    {children}
                </div>
            </div>
        </div>
    );
}

export default AuthWrapper;